import {
    confirmResetPassword,
    confirmSignIn,
    confirmSignUp,
    fetchAuthSession,
    getCurrentUser,
    resendSignUpCode,
    resetPassword,
    signIn,
    signOut,
    signUp,
} from 'aws-amplify/auth';
import { isValidString } from '../common/CommonUtilsMethods';
import { configureWorkyAmplify, getWorkyAmplifySpaClientId } from './WorkyAmplifyConfig';
import type {
    CognitoAuthTokensResult,
    CognitoConfirmPasswordResetRequest,
    CognitoConfirmSignUpRequest,
    CognitoExistingSessionResult,
    CognitoPasswordResetFailureResult,
    CognitoSignInOutcome,
    CognitoSignInWithPasswordRequest,
    CognitoSignUpFailureResult,
    CognitoSignUpRequest,
} from './WorkyCognitoAuthServiceInterfaces';
import { CognitoSignInOutcomeType } from './WorkyCognitoAuthServiceInterfaces';
import { isCognitoUserNotConfirmedError } from './WorkyCognitoAuthErrorService';
import { buildCognitoSignUpFailureResult, buildGenericCognitoSignUpFailureResult } from './WorkySignUpFailureService';
import { CognitoMfaChallengeStep } from './WorkyPendingMfaChallengeInterfaces';
import { CognitoNewPasswordChallengeStep } from './WorkyPendingNewPasswordChallengeInterfaces';

const containsOnlyNumbers = (input: string): boolean => /^\d+$/.test(input);

const formatWorkyPhoneNumberForCognito = (rawPhoneNumber: string): string | null => {
    const trimmedInput: string = rawPhoneNumber.trim();
    if (!isValidString(trimmedInput)) {
        return null;
    }

    const compactInput: string = trimmedInput.replace(/\s/g, '');
    const e164Pattern: RegExp = /^\+[1-9]\d{10,14}$/;
    if (e164Pattern.test(compactInput)) {
        return compactInput;
    }

    const numericDigits: string =
        trimmedInput.length === 10 && containsOnlyNumbers(trimmedInput)
            ? trimmedInput
            : trimmedInput.replace(/[^0-9]/g, '');

    if (numericDigits.length === 10) {
        return `+1${numericDigits}`;
    }

    if (numericDigits.length === 11 && numericDigits.charAt(0) === '1') {
        return `+${numericDigits}`;
    }

    return null;
};

const isValidRegistrationPhoneNumber = (rawPhoneNumber: string): boolean => {
    return formatWorkyPhoneNumberForCognito(rawPhoneNumber) !== null;
};

const decodeJwtPayload = (jwtToken: string): Record<string, unknown> | null => {
    try {
        const tokenParts: readonly string[] = jwtToken.split('.');
        if (tokenParts.length < 2) {
            return null;
        }
        const encodedPayload: string = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
        const decodedPayload: string = atob(encodedPayload);
        return JSON.parse(decodedPayload) as Record<string, unknown>;
    } catch {
        return null;
    }
};

const computeExpiresInSecondsFromAccessToken = (accessToken: string): number | undefined => {
    const payload: Record<string, unknown> | null = decodeJwtPayload(accessToken);
    const expirationEpochSeconds: unknown = payload?.exp;
    if (typeof expirationEpochSeconds !== 'number') {
        return undefined;
    }
    const currentEpochSeconds: number = Math.floor(Date.now() / 1000);
    return Math.max(expirationEpochSeconds - currentEpochSeconds, 0);
};

const resolveSignedInEmailFromIdToken = (idToken: string, fallbackUsername: string): string => {
    const payload: Record<string, unknown> | null = decodeJwtPayload(idToken);
    const emailFromToken: unknown = payload?.email;
    if (typeof emailFromToken === 'string' && isValidString(emailFromToken)) {
        return emailFromToken.trim().toLowerCase();
    }
    return fallbackUsername.trim().toLowerCase();
};

const readCognitoRefreshTokenFromBrowserStorage = (
    clientId: string,
    username: string
): string | null => {
    const directStorageKey: string = `CognitoIdentityServiceProvider.${clientId}.${username}.refreshToken`;
    const directRefreshToken: string | null = localStorage.getItem(directStorageKey);
    if (isValidString(directRefreshToken)) {
        return directRefreshToken;
    }

    const lowerCaseUsername: string = username.toLowerCase();
    if (lowerCaseUsername !== username) {
        const lowerCaseStorageKey: string = `CognitoIdentityServiceProvider.${clientId}.${lowerCaseUsername}.refreshToken`;
        const lowerCaseRefreshToken: string | null = localStorage.getItem(lowerCaseStorageKey);
        if (isValidString(lowerCaseRefreshToken)) {
            return lowerCaseRefreshToken;
        }
    }

    for (let storageIndex: number = 0; storageIndex < localStorage.length; storageIndex += 1) {
        const storageKey: string | null = localStorage.key(storageIndex);
        if (
            storageKey !== null &&
            storageKey.includes(clientId) &&
            storageKey.endsWith('.refreshToken')
        ) {
            const refreshTokenCandidate: string | null = localStorage.getItem(storageKey);
            if (isValidString(refreshTokenCandidate)) {
                return refreshTokenCandidate;
            }
        }
    }

    return null;
};

const resolveCognitoAuthTokensAfterSignIn = async (): Promise<CognitoAuthTokensResult | null> => {
    const authSession = await fetchAuthSession();
    const accessTokenJwt = authSession.tokens?.accessToken;
    const idTokenJwt = authSession.tokens?.idToken;

    if (accessTokenJwt === undefined || idTokenJwt === undefined) {
        return null;
    }

    const accessToken: string = accessTokenJwt.toString();
    const idToken: string = idTokenJwt.toString();
    const currentUser = await getCurrentUser();
    const refreshToken: string | null = readCognitoRefreshTokenFromBrowserStorage(
        getWorkyAmplifySpaClientId(),
        currentUser.username
    );

    if (!isValidString(refreshToken)) {
        return null;
    }

    return {
        accessToken,
        idToken,
        refreshToken,
        expiresInSeconds: computeExpiresInSecondsFromAccessToken(accessToken),
    };
};

/**
 * Returns Cognito tokens when Amplify already has an authenticated session.
 * Used to finish the Worky token bridge without calling signIn again.
 */
const getExistingCognitoAuthSession = async (): Promise<CognitoExistingSessionResult | null> => {
    const amplifyReady: boolean = configureWorkyAmplify();
    if (!amplifyReady) {
        return null;
    }

    try {
        const currentUser = await getCurrentUser();
        const cognitoTokens: CognitoAuthTokensResult | null = await resolveCognitoAuthTokensAfterSignIn();
        if (cognitoTokens === null) {
            return null;
        }

        return {
            tokens: cognitoTokens,
            signedInEmailAddress: resolveSignedInEmailFromIdToken(
                cognitoTokens.idToken,
                currentUser.username
            ),
        };
    } catch {
        return null;
    }
};

const COGNITO_IDENTITY_PROVIDER_STORAGE_PREFIX: string = 'CognitoIdentityServiceProvider.';

const clearCognitoIdentityProviderLocalStorage = (): void => {
    const storageKeysToRemove: string[] = [];

    for (let storageIndex: number = 0; storageIndex < localStorage.length; storageIndex += 1) {
        const storageKey: string | null = localStorage.key(storageIndex);
        if (
            storageKey !== null &&
            storageKey.startsWith(COGNITO_IDENTITY_PROVIDER_STORAGE_PREFIX)
        ) {
            storageKeysToRemove.push(storageKey);
        }
    }

    for (const storageKey of storageKeysToRemove) {
        localStorage.removeItem(storageKey);
    }
};

const signOutCognitoSession = async (): Promise<void> => {
    const amplifyReady: boolean = configureWorkyAmplify();
    if (amplifyReady) {
        try {
            await signOut();
        } catch (error: unknown) {
            console.error('Cognito sign-out failed', error);
        }
    }

    clearCognitoIdentityProviderLocalStorage();
};

const resolveMfaChallengeStepFromAmplifySignInStep = (
    amplifySignInStep: string
): CognitoMfaChallengeStep | null => {
    if (amplifySignInStep === CognitoMfaChallengeStep.CONFIRM_SIGN_IN_WITH_SMS_CODE) {
        return CognitoMfaChallengeStep.CONFIRM_SIGN_IN_WITH_SMS_CODE;
    }
    if (amplifySignInStep === CognitoMfaChallengeStep.CONFIRM_SIGN_IN_WITH_TOTP_CODE) {
        return CognitoMfaChallengeStep.CONFIRM_SIGN_IN_WITH_TOTP_CODE;
    }
    if (amplifySignInStep === CognitoMfaChallengeStep.CONTINUE_SIGN_IN_WITH_MFA_SELECTION) {
        return CognitoMfaChallengeStep.CONTINUE_SIGN_IN_WITH_MFA_SELECTION;
    }
    return null;
};

const isNewPasswordRequiredAmplifySignInStep = (amplifySignInStep: string): boolean => {
    return (
        amplifySignInStep ===
            CognitoNewPasswordChallengeStep.CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED ||
        amplifySignInStep === 'NEW_PASSWORD_REQUIRED' ||
        amplifySignInStep === 'FORCE_CHANGE_PASSWORD'
    );
};

const buildSignInFailureOutcome = (errorMessage: string): CognitoSignInOutcome => {
    return {
        outcomeType: CognitoSignInOutcomeType.FAILURE,
        errorMessage,
    };
};

const resolveSignInOutcomeAfterAmplifyStep = async (
    emailAddress: string,
    amplifySignInStep: string,
    isSignedIn: boolean
): Promise<CognitoSignInOutcome> => {
    if (amplifySignInStep === 'DONE' || isSignedIn) {
        const cognitoTokens: CognitoAuthTokensResult | null = await resolveCognitoAuthTokensAfterSignIn();
        if (cognitoTokens === null) {
            return buildSignInFailureOutcome('Unable to retrieve sign-in tokens. Please try again.');
        }
        return {
            outcomeType: CognitoSignInOutcomeType.TOKENS,
            tokens: cognitoTokens,
        };
    }

    const mfaChallengeStep: CognitoMfaChallengeStep | null =
        resolveMfaChallengeStepFromAmplifySignInStep(amplifySignInStep);
    if (mfaChallengeStep !== null) {
        return {
            outcomeType: CognitoSignInOutcomeType.MFA_REQUIRED,
            emailAddress,
            mfaChallengeStep,
        };
    }

    if (isNewPasswordRequiredAmplifySignInStep(amplifySignInStep)) {
        return {
            outcomeType: CognitoSignInOutcomeType.NEW_PASSWORD_REQUIRED,
            emailAddress,
        };
    }

    if (amplifySignInStep === 'CONFIRM_SIGN_UP') {
        return {
            outcomeType: CognitoSignInOutcomeType.EMAIL_NOT_VERIFIED,
            emailAddress,
        };
    }

    return buildSignInFailureOutcome(
        'Additional sign-in steps are required. Please contact support.'
    );
};

const signInWithEmailPassword = async (
    request: CognitoSignInWithPasswordRequest
): Promise<CognitoSignInOutcome> => {
    const normalizedEmailAddress: string = request.emailAddress.trim().toLowerCase();

    if (!isValidString(normalizedEmailAddress) || !isValidString(request.password)) {
        return buildSignInFailureOutcome('Email and password are required.');
    }

    const amplifyReady: boolean = configureWorkyAmplify();
    if (!amplifyReady) {
        return buildSignInFailureOutcome('Sign-in is not configured for this environment.');
    }

    try {
        const existingSession: CognitoExistingSessionResult | null =
            await getExistingCognitoAuthSession();

        if (existingSession !== null) {
            if (existingSession.signedInEmailAddress === normalizedEmailAddress) {
                return {
                    outcomeType: CognitoSignInOutcomeType.TOKENS,
                    tokens: existingSession.tokens,
                };
            }
            await signOutCognitoSession();
        }

        const signInResult = await signIn({
            username: normalizedEmailAddress,
            password: request.password,
        });

        return resolveSignInOutcomeAfterAmplifyStep(
            normalizedEmailAddress,
            signInResult.nextStep.signInStep,
            signInResult.isSignedIn
        );
    } catch (error: unknown) {
        if (isCognitoUserNotConfirmedError(error)) {
            console.error(
                'Cognito sign-in requires email verification for email {}',
                normalizedEmailAddress,
                error
            );
            return {
                outcomeType: CognitoSignInOutcomeType.EMAIL_NOT_VERIFIED,
                emailAddress: normalizedEmailAddress,
            };
        }

        const errorMessage: string =
            error instanceof Error ? error.message : 'Sign-in failed. Please check your credentials.';
        console.error('Cognito sign-in failed for email {}', normalizedEmailAddress, error);
        return buildSignInFailureOutcome(errorMessage);
    }
};

const confirmMfaSignInChallengeResponse = async ({
    emailAddress,
    challengeResponse,
}: {
    emailAddress: string;
    challengeResponse: string;
}): Promise<CognitoSignInOutcome> => {
    const normalizedEmailAddress: string = emailAddress.trim().toLowerCase();
    const normalizedChallengeResponse: string = challengeResponse.trim();

    if (!isValidString(normalizedEmailAddress) || !isValidString(normalizedChallengeResponse)) {
        return buildSignInFailureOutcome('Verification response is required.');
    }

    const amplifyReady: boolean = configureWorkyAmplify();
    if (!amplifyReady) {
        return buildSignInFailureOutcome('Sign-in is not configured for this environment.');
    }

    try {
        const confirmSignInResult = await confirmSignIn({
            challengeResponse: normalizedChallengeResponse,
        });

        return resolveSignInOutcomeAfterAmplifyStep(
            normalizedEmailAddress,
            confirmSignInResult.nextStep.signInStep,
            confirmSignInResult.isSignedIn
        );
    } catch (error: unknown) {
        const errorMessage: string =
            error instanceof Error
                ? error.message
                : 'Verification failed. Please check your code and try again.';
        console.error(
            'Cognito MFA confirm sign-in failed for email {}',
            normalizedEmailAddress,
            error
        );
        return buildSignInFailureOutcome(errorMessage);
    }
};

const confirmNewPasswordSignInChallenge = async ({
    emailAddress,
    newPassword,
}: {
    emailAddress: string;
    newPassword: string;
}): Promise<CognitoSignInOutcome> => {
    const normalizedEmailAddress: string = emailAddress.trim().toLowerCase();

    if (!isValidString(normalizedEmailAddress) || !isValidString(newPassword)) {
        return buildSignInFailureOutcome('A new password is required.');
    }

    const amplifyReady: boolean = configureWorkyAmplify();
    if (!amplifyReady) {
        return buildSignInFailureOutcome('Sign-in is not configured for this environment.');
    }

    try {
        const confirmSignInResult = await confirmSignIn({
            challengeResponse: newPassword,
            options: {
                userAttributes: {
                    email: normalizedEmailAddress,
                },
            },
        });

        return resolveSignInOutcomeAfterAmplifyStep(
            normalizedEmailAddress,
            confirmSignInResult.nextStep.signInStep,
            confirmSignInResult.isSignedIn
        );
    } catch (error: unknown) {
        const errorMessage: string =
            error instanceof Error
                ? error.message
                : 'Unable to set your new password. Please try again.';
        console.error(
            'Cognito new-password confirm sign-in failed for email {}',
            normalizedEmailAddress,
            error
        );
        return buildSignInFailureOutcome(errorMessage);
    }
};

const signUpWithEmailPassword = async (
    request: CognitoSignUpRequest
): Promise<CognitoSignUpFailureResult | null> => {
    const normalizedEmailAddress: string = request.emailAddress.trim().toLowerCase();

    if (!isValidString(normalizedEmailAddress) || !isValidString(request.password)) {
        return buildGenericCognitoSignUpFailureResult('Email and password are required.');
    }

    if (!isValidString(request.phoneNumber)) {
        return buildGenericCognitoSignUpFailureResult('Phone number is required.');
    }

    const formattedPhoneNumber: string | null = formatWorkyPhoneNumberForCognito(request.phoneNumber);
    if (formattedPhoneNumber === null) {
        return buildGenericCognitoSignUpFailureResult('Please enter a valid phone number.');
    }

    const amplifyReady: boolean = configureWorkyAmplify();
    if (!amplifyReady) {
        return buildGenericCognitoSignUpFailureResult('Sign-up is not configured for this environment.');
    }

    try {
        const userAttributes: Record<string, string> = {
            email: normalizedEmailAddress,
            given_name: request.firstName.trim(),
            family_name: request.lastName.trim(),
            phone_number: formattedPhoneNumber,
        };

        await signUp({
            username: normalizedEmailAddress,
            password: request.password,
            options: {
                userAttributes,
            },
        });

        return null;
    } catch (error: unknown) {
        console.error('Cognito sign-up failed for email {}', normalizedEmailAddress, error);
        return buildCognitoSignUpFailureResult(error);
    }
};

const confirmSignUpWithCode = async (
    request: CognitoConfirmSignUpRequest
): Promise<CognitoSignUpFailureResult | null> => {
    const normalizedEmailAddress: string = request.emailAddress.trim().toLowerCase();

    if (!isValidString(normalizedEmailAddress) || !isValidString(request.confirmationCode)) {
        return buildGenericCognitoSignUpFailureResult('Email and verification code are required.');
    }

    const amplifyReady: boolean = configureWorkyAmplify();
    if (!amplifyReady) {
        return buildGenericCognitoSignUpFailureResult('Sign-up is not configured for this environment.');
    }

    try {
        await confirmSignUp({
            username: normalizedEmailAddress,
            confirmationCode: request.confirmationCode.trim(),
        });
        return null;
    } catch (error: unknown) {
        const errorMessage: string =
            error instanceof Error ? error.message : 'Verification failed. Please check your code.';
        console.error('Cognito confirm sign-up failed for email {}', normalizedEmailAddress, error);
        return buildGenericCognitoSignUpFailureResult(errorMessage);
    }
};

const resendSignUpConfirmationCode = async (
    emailAddress: string
): Promise<CognitoSignUpFailureResult | null> => {
    const normalizedEmailAddress: string = emailAddress.trim().toLowerCase();

    if (!isValidString(normalizedEmailAddress)) {
        return buildGenericCognitoSignUpFailureResult('Email is required.');
    }

    const amplifyReady: boolean = configureWorkyAmplify();
    if (!amplifyReady) {
        return buildGenericCognitoSignUpFailureResult('Sign-up is not configured for this environment.');
    }

    try {
        await resendSignUpCode({ username: normalizedEmailAddress });
        return null;
    } catch (error: unknown) {
        const errorMessage: string =
            error instanceof Error ? error.message : 'Unable to resend verification code.';
        console.error('Cognito resend sign-up code failed for email {}', normalizedEmailAddress, error);
        return buildGenericCognitoSignUpFailureResult(errorMessage);
    }
};

const requestCognitoPasswordReset = async (
    emailAddress: string
): Promise<CognitoPasswordResetFailureResult | null> => {
    const normalizedEmailAddress: string = emailAddress.trim().toLowerCase();
    if (!isValidString(normalizedEmailAddress)) {
        return { errorMessage: 'Email is required.' };
    }

    const amplifyReady: boolean = configureWorkyAmplify();
    if (!amplifyReady) {
        return { errorMessage: 'Password recovery is not configured for this environment.' };
    }

    try {
        await resetPassword({ username: normalizedEmailAddress });
        return null;
    } catch (error: unknown) {
        const errorMessage: string =
            error instanceof Error ? error.message : 'Unable to send a password reset code.';
        console.error('Cognito password reset request failed for email {}', normalizedEmailAddress, error);
        return { errorMessage };
    }
};

const confirmCognitoPasswordReset = async (
    request: CognitoConfirmPasswordResetRequest
): Promise<CognitoPasswordResetFailureResult | null> => {
    const normalizedEmailAddress: string = request.emailAddress.trim().toLowerCase();
    const normalizedConfirmationCode: string = request.confirmationCode.trim();

    if (!isValidString(normalizedEmailAddress) || !isValidString(normalizedConfirmationCode)) {
        return { errorMessage: 'Email and verification code are required.' };
    }
    if (!isValidString(request.newPassword)) {
        return { errorMessage: 'A new password is required.' };
    }

    const amplifyReady: boolean = configureWorkyAmplify();
    if (!amplifyReady) {
        return { errorMessage: 'Password recovery is not configured for this environment.' };
    }

    try {
        await confirmResetPassword({
            username: normalizedEmailAddress,
            confirmationCode: normalizedConfirmationCode,
            newPassword: request.newPassword,
        });
        return null;
    } catch (error: unknown) {
        const errorMessage: string =
            error instanceof Error ? error.message : 'Unable to reset your password.';
        console.error('Cognito password reset confirmation failed for email {}', normalizedEmailAddress, error);
        return { errorMessage };
    }
};

export {
    confirmCognitoPasswordReset,
    confirmMfaSignInChallengeResponse,
    confirmNewPasswordSignInChallenge,
    confirmSignUpWithCode,
    formatWorkyPhoneNumberForCognito,
    getExistingCognitoAuthSession,
    isValidRegistrationPhoneNumber,
    requestCognitoPasswordReset,
    resendSignUpConfirmationCode,
    signInWithEmailPassword,
    signOutCognitoSession,
    signUpWithEmailPassword,
};
